import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Table, Switch, Flex } from 'antd'

// Shared between the two pinout tables — the legacy one (components/PinTable)
// and the functional diagram one (FunctionalDiagrams/ConnectorPinout/PinoutTable)
// — so both answer the same gestures the same way.

// Pin-level row selection. A pin can occupy more than one row (several wires on
// a cavity, jumpers), so the pin — not the row — is the unit of selection:
// checking any of a pin's rows checks them all, and unchecking one releases the
// pin. Rows with no pin (empty-cavity fillers) cannot be selected.
//
// antd routes the header checkbox to onSelectAll and shift-click ranges to
// onSelectMultiple, both handing over the changed rows in the same
// (selected, selectedRows, changeRows) shape. A plain onChange would hand over
// the whole selected row set instead, which makes a multi-row pin impossible to
// deselect: unchecking one of its rows leaves the sibling selected, so the pin
// stays in the set and both rows are re-checked.
export const pinRowSelection = ({
  // A pinout that is still loading has no rows yet: this is built during render,
  // before the request resolves, so it must tolerate that rather than throw and
  // take the whole page down with it.
  rows = [],
  rowKey,
  getPin,
  selectedPins,
  onPinsChange,
  hideSelectAll = false,
}) => {
  const toggle = (pins, selected) =>
    onPinsChange(
      selected
        ? [...new Set([...selectedPins, ...pins])]
        : selectedPins.filter((pin) => !pins.includes(pin)),
    )

  const toggleChangedRows = (selected, _selectedRows, changeRows) =>
    toggle(changeRows.map(getPin).filter(Boolean), selected)

  return {
    type: 'checkbox',
    hideSelectAll,
    selectedRowKeys: rows
      .filter((row) => selectedPins.includes(getPin(row)))
      .map((row) => row[rowKey]),
    getCheckboxProps: (row) => ({ disabled: !getPin(row) }),
    onSelect: (row, selected) => toggle([getPin(row)], selected),
    onSelectAll: toggleChangedRows,
    onSelectMultiple: toggleChangedRows,
  }
}

// The selection column antd inserts is not in `columns`, so the summary row has
// to span one more cell when the table is selectable.
export const summaryColSpan = (columns, selectable) =>
  columns.length + (selectable ? 1 : 0)

// The bar above a pinout: the All Pins switch, plus whatever action that pinout
// offers for the pins the user picked.
//
// This is a render function, NOT a component, and must stay one. rc-table
// decides whether the summary sits above the body or below it with an identity
// check on what `summary` returns — `summaryNode.type === Summary &&
// summaryNode.props.fixed` (rc-table `Table.js`). Wrapping `Table.Summary` in a
// component of our own makes that check fail and silently drops the bar to the
// bottom of the table.
export const renderPinoutSummary = ({
  colSpan,
  isAllPins,
  setIsAllPins,
  loading = false,
  showAllPinsToggle = true,
  children,
}) => (
  <Table.Summary fixed="top">
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={colSpan}>
        <Flex align="center" gap="small">
          {showAllPinsToggle && (
            <Switch
              checkedChildren={<FormattedMessage id="pinout.buttons.allPins" />}
              unCheckedChildren={
                <FormattedMessage id="pinout.buttons.allPins" />
              }
              checked={isAllPins}
              onChange={() => setIsAllPins(!isAllPins)}
              loading={loading}
            />
          )}
          {children}
        </Flex>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  </Table.Summary>
)
